import { assign, cloneDeep, find, findIndex } from '@seiue/util'
import {
  BlockTypeEnum,
  PaperAnswer,
  PaperAnswerReq,
  PaperBlock,
  PaperQuestion,
  PaperReview,
  QuestionTypeEnum,
  ReviewResultEnum,
  TaskReflection,
} from 'packages/sdks-next/growp'
import { precisionFormat } from 'packages/utils'

/**
 * 获取主题目
 * @param questions - 所有题目
 * @returns 主题目
 */
export const getMainQuestion = (questions: PaperQuestion[]) => {
  const parentQuestions = questions.filter((q) => q.parentId === 0)
  if (parentQuestions.length > 0) {
    return parentQuestions[0]
  }
  return undefined
}

/**
 * 通过parentId筛选子题并根据weight排序
 * @param questions - 所有题目(子题)
 * @param parentId - parentId
 * @returns 筛选出的子题
 */
export const getSubQuestionsByParentId = (
  questions: PaperQuestion[],
  parentId: number
) => {
  return questions
    .filter((q) => q.parentId === parentId)
    .sort((qa, qb) => (qa.weight || 0) - (qb.weight || 0))
}

/**
 * 根据问题ID获取对应作答value
 * @param answers - 用户填写的所有答案
 * @param questionId - 问题ID
 * @returns ID对应的填写的答案
 */
export const getValueById = (answers: PaperAnswer[], questionId: number) => {
  const answerObj = find(answers, { questionId })
  return answerObj
    ? {
        value: answerObj.value,
        answer: {
          answerId: answerObj.id,
          questionId: answerObj.questionId,
          paperId: answerObj.paperId,
          bizId: answerObj.bizId,
          bizType: answerObj.bizType,
          activityTaskId: answerObj?.activityTaskId,
          submitterId: answerObj.submitterId,
          submitterDomain: answerObj.submitterDomain,
        },
      }
    : { value: undefined, answerObj: undefined }
}

/**
 * 根据题目ID匹配review内容
 * @param reviews - 批阅内容
 * @param questionId - 问题ID
 * @returns 批阅得分
 */
export const getReviewById = (reviews: PaperReview[], questionId: number) => {
  const reviewObj = find(reviews, { questionId })
  return reviewObj
    ? {
        gainScore: reviewObj.gainedScore,
        result: reviewObj.result,
        review: reviewObj,
      }
    : {}
}

/**
 * 更新用户填写的答案
 * @param answers - 用户填写的所有答案
 * @param obj - 要更新的答案
 * @returns 更新后的所有答案
 */
export const updateAnswers = (
  answers: PaperAnswerReq[],
  obj: PaperAnswerReq
) => {
  const { questionId, value } = obj
  const allAnswers = cloneDeep(answers)
  const answerIndex = findIndex(allAnswers, { questionId })
  if (answerIndex !== -1) {
    assign(allAnswers[answerIndex], { value })
  } else {
    allAnswers.push(obj)
  }
  return allAnswers
}

export const modeToText = {
  [ReviewResultEnum.Right]: '正确',
  [ReviewResultEnum.Wrong]: '错误',
  [ReviewResultEnum.HalfRight]: '半对',
}

export const modeToIconName = {
  [ReviewResultEnum.Right]: 'CheckMark',
  [ReviewResultEnum.Wrong]: 'WrongMark',
  [ReviewResultEnum.HalfRight]: 'HalfRight',
}

export const modeToColors = {
  [ReviewResultEnum.Right]: ['#00ac34', '#e8ffea', '#7ff59f'],
  [ReviewResultEnum.Wrong]: ['#CB2634', '#FFECE8', '#FFA3AC'],
  [ReviewResultEnum.HalfRight]: ['#D26900', '#FFF8E8', '#FFC17A'],
}

export const getUserInfoById = (users: TaskReflection[], rid: number) => {
  const user = find(users, { rid })
  return user
}

/**
 * 根据题目blockId获取主题目编号
 */
export const getMainQuestionStrNum = (
  blocks: PaperBlock[],
  blockId: number
) => {
  let num = 0
  blocks
    .filter((b) => b.type === BlockTypeEnum.Question)
    .map((block, index) => {
      if (block.id === blockId) {
        num = index + 1
      }
    })
  return num
}

/**
 * 为题目块编号
 * @param blocks - 所有块
 */
export const AddSerialNumberForQuestionBlock = (
  blocks: PaperBlock[]
): (PaperBlock & { serialNumber?: number })[] => {
  let count = 0
  const finalBlocks = blocks.map((block) => {
    if (block.type === BlockTypeEnum.Question) {
      count++
      return {
        ...block,
        serialNumber: count,
      }
    }
    return block
  })
  return finalBlocks
}

/**
 * 格式化题干
 * @param param - 参数
 * @param param.type - 测验类型
 * @param param.stem - 题干
 * @param param.score - 分数
 * @param param.showScore - 是否显示得分
 * @returns stem string
 */
export const formatStem = ({
  type,
  stem,
  score,
  showScore,
}: {
  type: QuestionTypeEnum
  stem?: string | null
  score?: string | null
  showScore: boolean
}) => {
  const matchStem = (stem || '<p></p>').match(/<[^>]+>/)
  const firstTag = matchStem ? matchStem[0] : ''
  const insertText = showScore
    ? `(${
        type === QuestionTypeEnum.MultipleChoice ? '多选' : ''
      }${precisionFormat(score || 0)}分)`
    : `${type === QuestionTypeEnum.MultipleChoice ? '(多选)' : ''}`

  const finalStem = (stem || '<p></p>').replace(
    firstTag,
    firstTag +
      `<span
    style='
    font-size: 14px;
    color: #86909C;
    line-height: 22px;
  '
    >${insertText}</span>`
  )
  return finalStem
}
